(ns frontend.view.settings.new-tenant-invite
  (:require [clojure.string :as string]
            [common.currencies :as currencies]
            [common.localization :as loc]
            [common.timezones :as timezones]
            [common.util :as cu]
            [frontend.conn :as conn]
            [frontend.routing :as routing]
            [frontend.state.current-session :as session]
            [frontend.utils :as fu]
            [frontend.view.settings.db :as db]
            [promesa.core :as p]
            [ui.components.alert.view :as alert]
            [ui.components.button.view :as button]))

(defn create-tenant! []
  (let [{:keys [new-tenant]} @db/state]
    ;; optimistic hide values
    (swap! db/state assoc :processing? true)
    (deref db/state)
    (-> (conn/sendp! :tenant/create-and-invite new-tenant)
        (p/then (fn [_]
                  (swap! db/state dissoc :new-tenant)
                  (swap! db/state dissoc :processing?)
                  (routing/set-view! [:settings-tenants])))
        (p/catch (fn [_err]
                   (swap! db/state dissoc :processing?))))))

(defn form-error []
  (let [{:keys [entities new-tenant]} @db/state
        tenants (filter (fn [entity]
                          (= (:entity/type entity) :entity.type/tenant))
                        (vals entities))
        existing-names (->> tenants
                            (map (fn [n]
                                   (string/lower-case (:tenant/name n))))
                            (set))
        existing-emails (->> tenants
                             (map (fn [e]
                                    (string/lower-case (:tenant.inbox/email e))))
                             (set))
        {:keys [tenant/name tenant.inbox/email
                tenant/default-currency tenant/timezone tenant/domain-primary
                tenant/customer-success-manager-email tenant/account-manager-email
                tenant/first-closer-email account/first-name
                account/last-name account/account-email]} new-tenant]
    (cond
      (string/blank? name) (loc/tr :tenant-name-cannot-be-empty)

      (string/blank? email) (loc/tr :tenant-email-cannot-be-empty)

      (contains? (into #{} existing-names) (string/lower-case name))
      (loc/tr :tenant-name-already-exists)

      (contains? (into #{} existing-emails) (string/lower-case email))
      (loc/tr :tenant-email-already-exists)

      (string/blank? domain-primary) (loc/tr :tenant-primary-domain-cannot-be-empty)
      (string/blank? customer-success-manager-email) (loc/tr :tenant-customer-success-email-cannot-be-empty)
      (string/blank? account-manager-email) (loc/tr :tenant-account-manager-email-cannot-be-empty)
      (string/blank? first-closer-email) (loc/tr :tenant-first-closer-email-cannot-be-empty)
      (not default-currency) (loc/tr :please-select-currency)
      (string/blank? timezone) (loc/tr :please-select-timezone)

      (and (string/blank? first-name)
           (string/blank? last-name)) (loc/tr :account-name-cannot-be-empty)

      (not (fu/validate-email account-email)) (loc/tr :account-email-not-valid))))

(defn update! [k v]
  (swap! db/state assoc-in [:new-tenant k] v))

(defn view []
  (fn []
    (let [{:keys [_entities new-tenant processing?]} @db/state
          {:keys [tenant.inbox/email
                  tenant/default-currency tenant/domain-primary tenant/customer-success-manager-email
                  tenant/account-manager-email tenant/first-closer-email
                  tenant/timezone account/first-name
                  account/last-name account/account-email]} new-tenant
          tenant-name (:tenant/name new-tenant)
          error-msg (form-error)]
      (cond processing?
            [:i.fal.fa-spinner.fa-pulse.fa-5x]

            (or (session/sys-admin?)
                (session/zadmin?))
            [:div
             {:class-name "container max-w-[1024px]"}
             [:div.card
              [:div.card-container__header
               [:div.title-h2 "Create New Tenant"]]
              [:div.card-container__body
               [:div
                {:class "flex flex-col gap-3"}
                [:div
                 {:style {:position         "relative"
                          :bottom           "5px"
                          :background-color "white"
                          :padding          "none"
                          :margin-bottom    "8px"}}

                 (when error-msg
                   [alert/warning
                    {}
                    error-msg])]

                [:div
                 [:label.input-editor__label.d-block.pb-2 (loc/tr :name)]
                 [:input.input-area
                  {:type      "text"
                   :value     (str tenant-name)
                   :on-change (fn [e]
                                (update! :tenant/name (-> e .-target .-value)))
                   :on-blur   (fn [e]
                                (update! :tenant/name (cu/str-trim (-> e .-target .-value))))}]]

                [:div
                 [:label.input-editor__label.d-block.pb-2 (loc/tr :case-entry-email-prefix)]
                 [:input.input-area
                  {:type      "text"
                   :value     email
                   :on-change #(update! :tenant.inbox/email (cu/str-trim-lcase (-> % .-target .-value)))}]]

                [:div
                 {:class "grid grid-cols-2 gap-3 gap-y-3"}
                 [:div
                  [:div
                   [:label.input-editor__label.d-block.pb-2 (loc/tr :tenant-currency)]
                   [:select
                    {:class-name "input-area"
                     :value      (or (when (keyword? default-currency)
                                       (name default-currency))
                                     "")
                     :on-change  #(let [value (-> % .-target .-value)]
                                    (if (= value "")
                                      (swap! db/state update-in [:new-tenant] dissoc :tenant/default-currency)
                                      (update! :tenant/default-currency (-> % .-target .-value (keyword)))))}
                    [:option {:key "" :value ""}]
                    (for [cur currencies/currencies
                          :let [[kw val] cur]]
                      ^{:key (str "currency-" val)}
                      [:option
                       {:value (name kw)}
                       val])]]]

                 [:div
                  [:div
                   [:label.input-editor__label.d-block.pb-2 (loc/tr :tenant-timezone)]
                   [:select
                    {:class-name "input-area"
                     :value      (or timezone "")
                     :on-change  #(let [value (-> % .-target .-value)]
                                    (if (= value "")
                                      (swap! db/state update-in [:new-tenant] dissoc :tenant/timezone)
                                      (update! :tenant/timezone (-> % .-target .-value))))}
                    [:option {:key "" :value ""}]
                    (for [tz timezones/timezones
                          :let [[val text-option] tz]]
                      ^{:key (str "timezone-" val)}
                      [:option
                       {:value val}
                       text-option])]]]]
                
                [:div
                 [:label.input-editor__label.d-block.pb-2 (loc/tr :primary-domain-for-email-addresses)]
                 [:input.input-area
                  {:type      "text"
                   :value     domain-primary
                   :on-change #(update! :tenant/domain-primary (cu/str-trim-lcase (-> % .-target .-value)))}]]

                [:div
                 [:label.input-editor__label.d-block.pb-2 (loc/tr :customer-success-manager-email)]
                 [:input.input-area
                  {:type      "text"
                   :value     customer-success-manager-email
                   :on-change #(update! :tenant/customer-success-manager-email (cu/str-trim-lcase (-> % .-target .-value)))}]]

                [:div
                 [:label.input-editor__label.d-block.pb-2 (loc/tr :account-manager-email)]
                 [:input.input-area
                  {:type      "text"
                   :value     account-manager-email
                   :on-change #(update! :tenant/account-manager-email (cu/str-trim-lcase (-> % .-target .-value)))}]]

                [:div
                 [:label.input-editor__label.d-block.pb-2 (loc/tr :first-closer-email)]
                 [:input.input-area
                  {:type      "text"
                   :value     first-closer-email
                   :on-change #(update! :tenant/first-closer-email (cu/str-trim-lcase (-> % .-target .-value)))}]]

                [:div
                 {:class "flex flex-col gap-3"}
                 [:div
                  {:class "text-xl"}
                  (loc/tr :first-admin-user)]
                 [:div
                  [:label.input-editor__label.d-block.pb-2 (loc/tr :first-name)]
                  [:div
                   [:input.input-area
                    {:type      "text"
                     :value     first-name
                     :on-change #(update! :account/first-name (cu/str-trim (-> % .-target .-value)))}]]]

                 [:div
                  [:label.input-editor__label.d-block.pb-2 (loc/tr :last-name)]
                  [:input.input-area
                   {:type      "text"
                    :value     last-name
                    :on-change #(update! :account/last-name (cu/str-trim (-> % .-target .-value)))}]]

                 [:div
                  [:label.input-editor__label.d-block.pb-2 (loc/tr :email)]
                  [:input.input-area
                   {:type      "text"
                    :value     account-email
                    :on-change #(update! :account/account-email (cu/str-trim-lcase (-> % .-target .-value)))}]]]

                [:div.py-3.text-center
                 [button/primary-solid
                  {:on-click  create-tenant!
                   :disabled? (not (string/blank? (form-error)))}
                  (loc/tr :create-and-invite)]]]]]]))))
